import * as React from 'react';

import {
  ValidationResult as AriaValidationResult,
  Text,
} from 'react-aria-components';

import { cn } from '../../../lib/stylingUtils';
import { FieldError, FieldGroup, Label } from '../Field';

// Individual input element for a single digit
const OTPFieldInput = ({
  value,
  index,
  isDisabled: disabled,
  onChange,
  onKeyUp,
  onPaste,
  onFocus,
  inputRef,
  className,
  ...props
}: OTPFieldInputProps) => (
  <input
    type="text"
    inputMode="numeric"
    maxLength={1}
    value={value}
    disabled={disabled}
    autoComplete={index === 0 ? 'one-time-code' : 'off'}
    className={cn(
      '@OTPFieldInput',
      'size-12 rounded-lg border-2 border-neutral text-center text-2xl',
      'disabled:bg-space disabled:text-foreground/40',
      className,
    )}
    onChange={onChange}
    onKeyUp={onKeyUp}
    onPaste={onPaste}
    onFocus={onFocus}
    ref={inputRef}
    {...props}
  />
);

interface OTPFieldProps extends Pick<
  React.HTMLAttributes<HTMLInputElement>,
  'className'
> {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: AriaValidationResult) => string);
  length?: number;
  onChange: (code: string) => void;
  isDisabled?: boolean;
  autoFocus?: boolean;
}

interface OTPFieldInputProps extends Pick<
  React.HTMLAttributes<HTMLInputElement>,
  'className' | 'onChange'
> {
  value: string;
  index: number;
  isDisabled?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyUp: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  onFocus: () => void;
  inputRef: (ref: HTMLInputElement | null) => void;
  className?: string;
}

const OTPField = ({
  label,
  description,
  errorMessage,
  length = 6,
  onChange,
  isDisabled: disabled,
  autoFocus = false,
  className,
}: OTPFieldProps) => {
  const [code, setCode] = React.useState<string[]>(Array(length).fill(''));
  const inputs = React.useRef<(HTMLInputElement | null)[]>([]);

  React.useEffect(() => {
    onChange(code.join(''));
  }, [code, onChange]);

  React.useEffect(() => {
    if (autoFocus) {
      inputs.current[0]?.focus();
    }
  }, [autoFocus]);

  const processInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    slot: number,
  ) => {
    const value = e.target.value;
    if (disabled) return;

    const newCode = [...code];
    newCode[slot] = value.substring(value.length - 1);
    setCode(newCode);

    if (slot !== length - 1 && value) {
      inputs.current[slot + 1]?.focus();
    }
  };

  const onKeyUp = (e: React.KeyboardEvent<HTMLInputElement>, slot: number) => {
    if (disabled) return;

    if (e.code === 'Backspace' && !code[slot] && slot !== 0) {
      const newCode = [...code];
      newCode[slot - 1] = '';
      setCode(newCode);
      inputs.current[slot - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    e.preventDefault();
    const paste = e.clipboardData.getData('text');
    const pasteArray = paste.split('').slice(0, length);

    const newCode = [...code];
    pasteArray.forEach((char, i) => {
      newCode[i] = char;
    });
    setCode(newCode);

    inputs.current[Math.min(pasteArray.length, length - 1)]?.focus();
  };

  const handleFocus = (slot: number) => {
    if (disabled) return;

    // Select the contents when focusing
    setTimeout(() => {
      inputs.current[slot]?.select();
    }, 0);
  };

  return (
    <div className={cn('@OTPField', 'group flex flex-col gap-2', className)}>
      {label && <Label>{label}</Label>}
      <FieldGroup>
        <div className="flex justify-center gap-2">
          {code.map((digit, idx) => (
            <OTPFieldInput
              key={idx}
              value={digit}
              index={idx}
              isDisabled={disabled}
              onChange={(e) => processInput(e, idx)}
              onKeyUp={(e) => onKeyUp(e, idx)}
              onPaste={(e) => handlePaste(e)}
              onFocus={() => handleFocus(idx)}
              inputRef={(ref) => (inputs.current[idx] = ref)}
            />
          ))}
        </div>
      </FieldGroup>
      {description && (
        <Text className="text-sm text-foreground/20" slot="description">
          {description}
        </Text>
      )}
      <FieldError>{errorMessage}</FieldError>
    </div>
  );
};

export { OTPField, OTPFieldInput };
export type { OTPFieldProps, OTPFieldInputProps };
